import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  ArrowDown, ArrowLeft, ArrowUp, GripVertical, Lock, Plus, Trash2,
} from "lucide-react";
import {
  DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { surveyApi } from "@/lib/apiClient";
import { T, hexA, todayJst, toDateInput, fromDateInput } from "@/uniq/theme";
import type {
  DistributionMode, Question, QuestionType, SurveyStatus,
} from "@shared-domain/survey/contract";
import {
  MAX_PHASE_CONDS, parsePhase, parseTokenPredicate, stringifyPhase,
  stripPhaseOptionValues, stripPhaseParents, type PhaseCond,
} from "@shared-domain/survey/logic";
import { describePhase, DELETED_OPTION_LABEL } from "@shared-domain/survey/phaseLabel";
import { QTYPE } from "@/uniq/questionMeta";
import { OptionMark } from "@/uniq/OptionMark";
import {
  buildFixedQuestions, hasImpactPreset, withPreset,
  IMPACTS_QID, SURVEY_PRESETS, type SurveyPresetId,
} from "@shared-domain/survey/presets";

const uid = () => crypto.randomUUID().slice(0, 8);

const newQuestion = (type: QuestionType): Question => {
  const base = { questionId: uid(), label: "", required: false, phase: "always" };
  if (type === "radio") return { ...base, type, options: [{ v: uid(), label: "選択肢1" }, { v: uid(), label: "選択肢2" }] };
  if (type === "checkbox") return { ...base, type, options: [{ v: uid(), label: "選択肢1" }, { v: uid(), label: "選択肢2" }] };
  if (type === "scale") return { ...base, type, scaleMin: 0, scaleMax: 10 };
  if (type === "date") return { ...base, type };
  return { ...base, type };
};

const inp: React.CSSProperties = { width: "100%", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px", fontSize: 14, boxSizing: "border-box" };

export default function SurveyBuilder() {
  const params = useParams();
  const navigate = useNavigate();
  const [surveyId, setSurveyId] = useState<string | undefined>(params.surveyId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [issuer, setIssuer] = useState("");
  const [deadline, setDeadline] = useState("");
  const [distribution, setDistribution] = useState<DistributionMode>("open");
  // 新規は固定必須設問 (ヒアリング相手・実施方法) を種付けした状態で開始。
  const [questions, setQuestions] = useState<Question[]>(() => (params.surveyId ? [] : buildFixedQuestions()));
  const [preset, setPreset] = useState<SurveyPresetId | "">("");
  const [status, setStatus] = useState<SurveyStatus>("draft");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!params.surveyId) return;
    surveyApi.get(params.surveyId).then((r) => {
      setTitle(r.survey.title); setDescription(r.survey.description);
      setIssuer(r.survey.issuer ?? ""); setDeadline(r.survey.deadline ?? "");
      setDistribution(r.survey.distribution); setQuestions(r.survey.questions); setStatus(r.survey.status);
      setPreset(hasImpactPreset(r.survey.questions) ? "impact-survey" : "");
    }).catch((e) => setError(String(e)));
  }, [params.surveyId]);

  const editable = status === "draft";
  const patch = (i: number, p: Partial<Question>) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? ({ ...q, ...p } as Question) : q)));
  // 分岐(表示条件)の親候補: 選択肢/離散値を持つ設問 (radio/checkbox/scale/date)。text は不可。
  const branchParents = questions.filter((q) => q.type === "radio" || q.type === "checkbox" || q.type === "scale" || q.type === "date");

  // 設問削除。削除対象を親とする他設問の分岐条件(cond)も剥がし、孤立分岐で保存が詰まらないようにする。
  const removeQuestion = (i: number) => setQuestions((qs) => {
    const target = qs[i];
    if (!target) return qs;
    const removed = new Set([target.questionId]);
    return qs.filter((_, idx) => idx !== i).map((q) => {
      const phase = stripPhaseParents(q.phase, removed);
      return phase === q.phase ? q : ({ ...q, phase } as Question);
    });
  });

  // radio 選択肢の削除。他設問の分岐値からも該当値を剥がす (不可視の残留値で保存が 400 になるのを防ぐ)。
  const removeRadioOption = (i: number, v: string) => setQuestions((qs) => {
    const target = qs[i];
    if (!target || target.type !== "radio") return qs;
    return qs.map((q, idx) => {
      if (idx === i && q.type === "radio")
        return { ...q, options: q.options.filter((o) => o.v !== v) } as Question;
      const phase = stripPhaseOptionValues(q.phase, target.questionId, [v]);
      return phase === q.phase ? q : ({ ...q, phase } as Question);
    });
  });

  // ⑥ 並べ替え: D&D (8px しきい値でクリックと区別) + 上下矢印のフォールバック。
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const moveQuestion = (i: number, dir: -1 | 1) => setQuestions((qs) => {
    const t = i + dir;
    if (t < 0 || t >= qs.length) return qs;
    return arrayMove(qs, i, t);
  });
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setQuestions((qs) => {
      const oldIndex = qs.findIndex((q) => q.questionId === active.id);
      const newIndex = qs.findIndex((q) => q.questionId === over.id);
      if (oldIndex < 0 || newIndex < 0) return qs;
      return arrayMove(qs, oldIndex, newIndex);
    });
  };

  // プリセット選択: 影響有無/影響項目を実施方法の直後に挿入 (なしで除去)。純関数 withPreset に委譲。
  const applyPreset = (p: SurveyPresetId | "") => {
    setPreset(p);
    setQuestions((qs) => withPreset(qs, p));
  };

  const save = async () => {
    setSaving(true); setError(null); setMsg(null);
    try {
      const body = { title, description, issuer, deadline, distribution, ...(status === "draft" ? { questions } : {}) };
      if (surveyId) { await surveyApi.update(surveyId, body); setMsg("保存しました"); }
      else { const r = await surveyApi.create({ ...body, questions }); setSurveyId(r.survey.surveyId); navigate(`/admin/${r.survey.surveyId}/edit`, { replace: true }); setMsg("作成しました"); }
    } catch (e) { setError(String(e)); } finally { setSaving(false); }
  };
  const changeStatus = async (next: SurveyStatus) => {
    if (!surveyId) return;
    try {
      await surveyApi.setStatus(surveyId, next);
      setStatus(next);
      setMsg(next === "published" ? "公開しました" : next === "draft" ? "非公開にしました（編集できます）" : "終了しました");
    } catch (e) { setError(String(e)); }
  };
  // アンケート削除 (公開中は不可・回答/割当も消えるため確認ダイアログを挟む)。
  const removeSurvey = async () => {
    if (!surveyId || status === "published" || deleting) return;
    if (!window.confirm("このアンケートを削除しますか？\n回答・割当のデータも削除され、元に戻せません。")) return;
    setDeleting(true); setError(null);
    try { await surveyApi.remove(surveyId); navigate("/admin"); }
    catch (e) { setError(String(e)); setDeleting(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <Link to="/admin" style={{ display: "flex", alignItems: "center", gap: 4, color: T.sub, textDecoration: "none", fontSize: 13 }}><ArrowLeft size={16} />一覧</Link>
          <span style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>{surveyId ? "アンケート編集" : "アンケート作成"}</span>
          <span style={{ fontSize: 11, background: "#F1F5F9", color: T.sub, borderRadius: 6, padding: "2px 8px" }}>{status === "draft" ? "下書き" : status === "published" ? "公開中" : "終了"}</span>
          {surveyId && (
            <button
              onClick={removeSurvey}
              disabled={status === "published" || deleting}
              title={status === "published" ? "公開中のアンケートは削除できません" : "アンケートを削除"}
              aria-label="アンケートを削除"
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", background: "transparent", border: 0, padding: "6px 8px", cursor: status === "published" || deleting ? "not-allowed" : "pointer", color: status === "published" ? "#CBD5E1" : T.danger, opacity: deleting ? 0.5 : 1 }}
            ><Trash2 size={18} /></button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "16px" }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 13, color: T.sub }}>タイトル<input style={{ ...inp, marginTop: 4 }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例: 原油価格 影響調査" /></label>
          <label style={{ fontSize: 13, color: T.sub }}>説明（任意）<textarea style={{ ...inp, marginTop: 4 }} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ fontSize: 13, color: T.sub, flex: 1 }}>発行元<input style={{ ...inp, marginTop: 4 }} value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="本部 業務部" /></label>
            <label style={{ fontSize: 13, color: T.sub, flex: 1 }}>期日<input type="date" style={{ ...inp, marginTop: 4 }} value={deadline.replace(/\//g, "-")} onChange={(e) => setDeadline(e.target.value ? e.target.value.replace(/-/g, "/") : "")} /></label>
          </div>
          <label style={{ fontSize: 13, color: T.sub }}>プリセット
            <select value={preset} disabled={!editable} onChange={(e) => applyPreset(e.target.value as SurveyPresetId | "")} style={{ ...inp, marginTop: 4 }}>
              <option value="">なし</option>
              {Object.values(SURVEY_PRESETS).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <span style={{ display: "block", fontSize: 11, color: T.faint, marginTop: 4 }}>「影響調査」を選ぶと実施方法の下に「影響有無」「影響項目」が自動でセットされます。</span>
          </label>
          <div>
            <span style={{ fontSize: 13, color: T.sub }}>配布方法</span>
            <div style={{ display: "flex", gap: 16, marginTop: 4, fontSize: 14 }}>
              {(["open", "targeted"] as const).map((d) => (
                <label key={d} style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <input type="radio" checked={distribution === d} disabled={!editable} onChange={() => setDistribution(d)} />
                  {d === "open" ? "自由（担当が顧客を選択）" : "指名（本部が顧客×担当を割当）"}
                </label>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12, color: T.sub, background: "#F1F5F9", borderRadius: 8, padding: "8px 10px" }}>
            <b>対象顧客名</b> は全調査の固定項目です（回答時に必ず選択されます）。<b>ヒアリング相手</b>・<b>実施方法</b> は既定で追加されますが、他の設問と同様に編集・削除できます。
          </div>
        </div>

        {surveyId && status !== "draft" && (
          <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", color: "#9A3412", borderRadius: 10, padding: "10px 12px", marginTop: 12, fontSize: 12.5, lineHeight: 1.6 }}>
            公開中/終了の調査は編集できません。<b>「非公開にして編集」</b>で下書きに戻すと修正できます。既に回答がある場合、設問を変更すると既存回答と対応が崩れることがあります。
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", marginTop: 20, marginBottom: 8 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: T.ink, margin: 0 }}>設問</h2>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={questions.map((q) => q.questionId)} strategy={verticalListSortingStrategy}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {questions.map((q, i) => {
            const TypeIcon = QTYPE[q.type].Icon;
            return (
            <SortableQuestionCard key={q.questionId} id={q.questionId} sortable={editable}>
            {(handle) => (
            <div style={{ background: "#fff", borderRadius: 12, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                {handle}
                {editable && (
                  <span style={{ display: "flex", flexDirection: "column", margin: "-3px 0" }}>
                    <button onClick={() => moveQuestion(i, -1)} disabled={i === 0} title="上に移動" aria-label="上に移動" style={{ background: "transparent", border: 0, padding: 1, cursor: i === 0 ? "not-allowed" : "pointer", color: i === 0 ? "#E2E8F0" : T.faint, display: "flex" }}><ArrowUp size={13} /></button>
                    <button onClick={() => moveQuestion(i, 1)} disabled={i === questions.length - 1} title="下に移動" aria-label="下に移動" style={{ background: "transparent", border: 0, padding: 1, cursor: i === questions.length - 1 ? "not-allowed" : "pointer", color: i === questions.length - 1 ? "#E2E8F0" : T.faint, display: "flex" }}><ArrowDown size={13} /></button>
                  </span>
                )}
                <span style={{ fontSize: 11, color: T.faint, fontWeight: 700 }}>Q{i + 1}</span>
                <span style={{ fontSize: 11, background: "#F1F5F9", color: T.sub, borderRadius: 6, padding: "1px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}><TypeIcon size={11} />{QTYPE[q.type].label}</span>
                <label style={{ marginLeft: "auto", fontSize: 12, color: T.faint, display: "flex", gap: 4, alignItems: "center" }}><input type="checkbox" checked={q.required} disabled={!editable} onChange={(e) => patch(i, { required: e.target.checked })} />必須</label>
                {editable && <button onClick={() => removeQuestion(i)} style={{ background: "transparent", border: 0, cursor: "pointer", color: "#CBD5E1" }}><Trash2 size={15} /></button>}
              </div>
              <input style={inp} value={q.label} disabled={!editable} placeholder="設問文" onChange={(e) => patch(i, { label: e.target.value })} />

              {q.type === "radio" && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {q.options.map((o, oi) => (
                    <div key={o.v} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <OptionMark kind="radio" />
                      <input style={{ ...inp, flex: 1 }} value={o.label} disabled={!editable} onChange={(e) => patch(i, { options: q.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)) } as Partial<Question>)} />
                      <select value={o.tone ?? ""} disabled={!editable} onChange={(e) => patch(i, { options: q.options.map((x, xi) => (xi === oi ? { ...x, tone: (e.target.value || undefined) as "ok" | "warn" | undefined } : x)) } as Partial<Question>)} style={{ ...inp, width: 90, flex: "none" }}>
                        <option value="">通常</option><option value="ok">OK(緑)</option><option value="warn">警告(橙)</option>
                      </select>
                      <label style={{ fontSize: 11, color: T.faint, display: "flex", gap: 3, alignItems: "center", flex: "none" }}><input type="checkbox" checked={!!o.withText} disabled={!editable} onChange={(e) => patch(i, { options: q.options.map((x, xi) => (xi === oi ? { ...x, withText: e.target.checked } : x)) } as Partial<Question>)} />自由記入</label>
                      {editable && q.options.length > 2 && <button onClick={() => removeRadioOption(i, o.v)} style={{ background: "transparent", border: 0, cursor: "pointer", color: "#CBD5E1" }}><Trash2 size={14} /></button>}
                    </div>
                  ))}
                  {editable && <button onClick={() => patch(i, { options: [...q.options, { v: uid(), label: `選択肢${q.options.length + 1}` }] } as Partial<Question>)} style={{ fontSize: 12, color: T.accent, background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>+ 選択肢を追加</button>}
                </div>
              )}
              {q.type === "checkbox" && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {q.options.map((o, oi) => (
                    <div key={o.v} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <OptionMark kind="checkbox" />
                      <input style={{ ...inp, flex: 1 }} value={o.label} disabled={!editable} placeholder={q.questionId === IMPACTS_QID ? `影響項目${oi + 1}` : `選択肢${oi + 1}`} onChange={(e) => patch(i, { options: q.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)) } as Partial<Question>)} />
                      {editable && q.options.length > 2 && <button onClick={() => patch(i, { options: q.options.filter((_, xi) => xi !== oi) } as Partial<Question>)} style={{ background: "transparent", border: 0, cursor: "pointer", color: "#CBD5E1" }}><Trash2 size={14} /></button>}
                    </div>
                  ))}
                  {editable && <button onClick={() => patch(i, { options: [...q.options, { v: uid(), label: `選択肢${q.options.length + 1}` }] } as Partial<Question>)} style={{ fontSize: 12, color: T.accent, background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>+ 選択肢を追加</button>}
                </div>
              )}
              {q.type === "scale" && (
                <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 13, alignItems: "center" }}>
                  <label>最小<input type="number" style={{ ...inp, width: 70, marginLeft: 4 }} value={q.scaleMin} disabled={!editable} onChange={(e) => patch(i, { scaleMin: Number(e.target.value) } as Partial<Question>)} /></label>
                  <label>最大<input type="number" style={{ ...inp, width: 70, marginLeft: 4 }} value={q.scaleMax} disabled={!editable} onChange={(e) => patch(i, { scaleMax: Number(e.target.value) } as Partial<Question>)} /></label>
                  <span style={{ fontSize: 11, color: T.faint }}>0〜10 で NPS</span>
                </div>
              )}
              {q.type === "text" && editable && <label style={{ fontSize: 12, color: T.faint, display: "flex", gap: 4, alignItems: "center", marginTop: 8 }}><input type="checkbox" checked={!!(q.type === "text" && q.multiline)} onChange={(e) => patch(i, { multiline: e.target.checked } as Partial<Question>)} />複数行</label>}

              {/* 分岐（条件付き表示）。draft では全設問で編集可、公開中/終了は読み取り表示のみ。 */}
              {editable && branchParents.some((p) => p.questionId !== q.questionId) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #E2E8F0", fontSize: 12, color: T.sub }}>
                  <BranchEditor q={q} parents={branchParents.filter((p) => p.questionId !== q.questionId)} onChange={(phase) => patch(i, { phase })} />
                </div>
              )}
              {!editable && q.phase !== "always" && (
                <LockedBranchCondition phase={q.phase} questions={questions} />
              )}
            </div>
            )}
            </SortableQuestionCard>
          );})}
          {questions.length === 0 && <p style={{ color: T.faint, fontSize: 13 }}>設問を追加してください。</p>}
        </div>
        </SortableContext>
        </DndContext>

        {/* 設問追加パレットはリストの下部 (追加のたび上へ戻らずに済む)。ボタン順は QTYPE のキー順。 */}
        {editable && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.faint, marginBottom: 6 }}>設問を追加</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(Object.keys(QTYPE) as QuestionType[]).map((t) => {
                const { label, Icon } = QTYPE[t];
                return (
                  <button key={t} onClick={() => setQuestions((qs) => [...qs, newQuestion(t)])} style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "5px 9px", fontSize: 12, color: "#334155", cursor: "pointer" }}><Plus size={12} /><Icon size={13} color={T.sub} />{label}</button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={save} disabled={saving || !title || !editable} style={{ background: T.accent, color: "#fff", border: 0, borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving || !title || !editable ? 0.5 : 1 }}>{saving ? "保存中…" : surveyId ? "保存" : "作成"}</button>
          {surveyId && status === "draft" && <button onClick={() => changeStatus("published")} disabled={questions.length === 0} style={{ background: "#fff", color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>公開する</button>}
          {surveyId && status === "published" && <button onClick={() => changeStatus("draft")} style={{ background: "#fff", color: "#9A3412", border: "1px solid #FED7AA", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>非公開にして編集</button>}
          {surveyId && status === "published" && <button onClick={() => changeStatus("closed")} style={{ background: "#fff", color: T.sub, border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}>終了する</button>}
          {surveyId && status === "closed" && <button onClick={() => changeStatus("draft")} style={{ background: "#fff", color: "#9A3412", border: "1px solid #FED7AA", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>非公開にして編集</button>}
          {msg && <span style={{ color: T.accent, fontSize: 13 }}>{msg}</span>}
          {error && <span style={{ color: "#dc2626", fontSize: 13 }}>{error}</span>}
        </div>

        {surveyId && distribution === "targeted" && (
          <div style={{ marginTop: 24, background: "#EAF2FB", border: "1px solid #CFE0F3", borderRadius: 12, padding: "12px 14px", fontSize: 12.5, color: "#334155", lineHeight: 1.6 }}>
            指名割当（顧客 × 担当）は一覧画面の <b>割当アイコン</b> から行います。
            <Link to={`/admin/${surveyId}/assign`} style={{ marginLeft: 8, color: T.accent, fontWeight: 700 }}>割当画面を開く →</Link>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 設問カードの sortable ラッパ (⑥)。ドラッグハンドルを render prop で渡し、
 * カード見た目は呼び出し側の既存 JSX を保つ。sortable=false (公開中/終了) はハンドル非表示。
 */
function SortableQuestionCard({ id, sortable, children }: {
  id: string; sortable: boolean; children: (handle: React.ReactNode) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !sortable });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const handle = sortable ? (
    <button type="button" {...attributes} {...listeners} title="ドラッグして並び替え" aria-label="ドラッグハンドル"
      style={{ background: "transparent", border: 0, padding: 2, color: T.faint, cursor: "grab", touchAction: "none", display: "flex" }}>
      <GripVertical size={15} />
    </button>
  ) : null;
  return <div ref={setNodeRef} style={style}>{children(handle)}</div>;
}

const chip = (on: boolean): React.CSSProperties => ({ border: `1px solid ${on ? T.accent : "#E2E8F0"}`, borderRadius: 9999, padding: "3px 10px", fontSize: 11, fontWeight: 600, background: on ? hexA(T.accent, 0.08) : "#fff", color: on ? T.accent : T.sub, cursor: "pointer" });
const dangerChip: React.CSSProperties = { border: `1px solid ${hexA(T.danger, 0.4)}`, borderRadius: 9999, padding: "3px 10px", fontSize: 11, fontWeight: 600, background: hexA(T.danger, 0.06), color: T.danger, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
// 分岐条件の各コントロールは幅を詰めて1行に収まりやすくする (日付の期間=2ピッカーでも折り返しにくく)。
const smallSelect: React.CSSProperties = { ...inp, width: "auto", flex: "none", padding: "5px 8px", fontSize: 12 };

/** 親設問型ごとの分岐 seed 値 (token 群)。radio/checkbox=先頭選択肢、scale/date=既定の比較 token。 */
const seedValues = (p: Question): string[] => {
  if (p.type === "radio" || p.type === "checkbox") return p.options[0] ? [p.options[0].v] : [];
  if (p.type === "scale") return [`>=${p.scaleMin}`];
  if (p.type === "date") return [`>=${todayJst()}`];
  return [];
};

/**
 * 表示条件エディタ。条件(cond)を複数持ち、条件間は「かつ」(AND)。各条件は親設問1つ +
 * 型に応じた値エディタ (radio/checkbox=選択肢チップ / scale=数値比較 / date=日付比較)。
 * 複数条件のときは左スパイン(縦ライン+ノード)で塊として連結し、切れ目を一目で分かるようにする。
 * 単一条件のときは従来どおり1行のまま。token 破壊防止のため編集は該当 cond の values のみ差し替える。
 */
function BranchEditor({ q, parents, onChange }: { q: Question; parents: Question[]; onChange: (phase: string) => void }) {
  const conds = parsePhase(q.phase) ?? [];
  const update = (next: PhaseCond[]) => onChange(stringifyPhase(next));
  const parentOf = (pid: string) => parents.find((x) => x.questionId === pid);
  const seedCond = (p: Question): PhaseCond => ({ parentId: p.questionId, values: seedValues(p) });
  const unused = parents.filter((r) => !conds.some((c) => c.parentId === r.questionId));
  const row: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };

  // 1条件分の中身 (親 select + 値エディタ + のとき + 削除)。単一表示とスパイン塊で共用。
  const condControls = (c: PhaseCond, ci: number) => {
    const usedElsewhere = new Set(conds.filter((_, xi) => xi !== ci).map((x) => x.parentId));
    const parent = parentOf(c.parentId);
    const setValues = (values: string[]) => update(conds.map((x, xi) => (xi === ci ? { ...x, values } : x)));
    const removeCond = () => update(conds.filter((_, xi) => xi !== ci));
    return (
      <>
        <select
          value={c.parentId}
          onChange={(e) => { const r = parentOf(e.target.value); if (r) update(conds.map((x, xi) => (xi === ci ? seedCond(r) : x))); }}
          style={smallSelect}
        >
          {!parent && <option value={c.parentId}>（削除された設問）</option>}
          {parents.filter((r) => !usedElsewhere.has(r.questionId)).map((r) => (
            <option key={r.questionId} value={r.questionId}>{r.label || "（無題の設問）"} が…</option>
          ))}
        </select>
        <CondValueEditor parent={parent} values={c.values} onChange={setValues} onRemoveCond={removeCond} />
        <span>のとき</span>
        <button onClick={removeCond} aria-label="この条件を削除" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#CBD5E1", display: "flex" }}><Trash2 size={13} /></button>
      </>
    );
  };

  const addButton = unused.length > 0 && conds.length < MAX_PHASE_CONDS && (
    <button onClick={() => { const r = unused[0]; if (r) update([...conds, seedCond(r)]); }} style={{ fontSize: 12, color: T.accent, background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>+ 条件を追加（かつ）</button>
  );

  // 0条件: 「常に表示」+ 親選択のプルダウン。
  if (conds.length === 0) {
    return (
      <div style={row}>
        <span>表示条件:</span>
        <select value="" onChange={(e) => { const r = parentOf(e.target.value); if (r) update([seedCond(r)]); }} style={smallSelect}>
          <option value="">常に表示</option>
          {parents.map((r) => <option key={r.questionId} value={r.questionId}>{r.label || "（無題の設問）"} が…</option>)}
        </select>
      </div>
    );
  }

  // 1条件: 従来どおり1行 (塊装飾なし)。
  if (conds.length === 1) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={row}><span>表示条件:</span>{condControls(conds[0]!, 0)}</div>
        {addButton}
      </div>
    );
  }

  // 2条件以上: 左スパインで塊として連結 (案3)。ノード間に「かつ」。
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ fontSize: 12, color: T.sub, fontWeight: 600 }}>表示条件（次をすべて満たすとき）</span>
      <div style={{ position: "relative", paddingLeft: 20 }}>
        <div style={{ position: "absolute", left: 6, top: 12, bottom: 12, width: 2, background: hexA(T.accent, 0.3), borderRadius: 2 }} />
        {conds.map((c, ci) => (
          <Fragment key={ci}>
            {ci > 0 && (
              <div style={{ position: "relative", height: 16, margin: "3px 0" }}>
                <span style={{ position: "absolute", left: -20, width: 26, textAlign: "center", fontSize: 10, fontWeight: 800, letterSpacing: ".05em", color: T.accent, background: T.card }}>かつ</span>
              </div>
            )}
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: -17, top: 12, width: 9, height: 9, borderRadius: 9999, background: T.card, border: `2px solid ${T.accent}`, boxSizing: "border-box" }} />
              <div style={{ ...row, background: "#F7FAFB", borderRadius: 10, padding: "9px 11px" }}>{condControls(c, ci)}</div>
            </div>
          </Fragment>
        ))}
      </div>
      {addButton}
    </div>
  );
}

/** cond の値エディタ。親設問型で出し分ける (親削除時は生値を除去可能チップで見せる)。 */
function CondValueEditor({ parent, values, onChange, onRemoveCond }: {
  parent: Question | undefined; values: string[]; onChange: (values: string[]) => void; onRemoveCond: () => void;
}) {
  if (parent && (parent.type === "radio" || parent.type === "checkbox"))
    return <ChoiceChips options={parent.options} values={values} onChange={onChange} onRemoveCond={onRemoveCond} />;
  if (parent && parent.type === "scale")
    return <ScalePredicate parent={parent} token={values[0] ?? ""} onChange={(t) => onChange([t])} />;
  if (parent && parent.type === "date")
    return <DatePredicate token={values[0] ?? ""} onChange={(t) => onChange([t])} />;
  // 親削除など解決不能: 生値を除去可能チップで見せる (残すと保存が拒否される)。
  return (
    <>
      {values.map((v) => (
        <button key={v} title="この値は解決できません。タップで条件から除去します"
          onClick={() => { const next = values.filter((x) => x !== v); if (next.length === 0) onRemoveCond(); else onChange(next); }}
          style={dangerChip}>{DELETED_OPTION_LABEL} ×</button>
      ))}
    </>
  );
}

/** radio/checkbox 用の選択肢トグルチップ (複数選択で OR) + 親に無い残留値の除去チップ。 */
function ChoiceChips({ options, values, onChange, onRemoveCond }: {
  options: { v: string; label: string }[]; values: string[]; onChange: (values: string[]) => void; onRemoveCond: () => void;
}) {
  return (
    <>
      {options.map((o) => {
        const on = values.includes(o.v);
        return (
          <button key={o.v}
            onClick={() => { const next = on ? values.filter((v) => v !== o.v) : [...values, o.v]; if (next.length === 0) return; onChange(next); }}
            title={on && values.length === 1 ? "最低1つの選択肢が必要です（条件ごと消すには右のゴミ箱）" : undefined}
            style={chip(on)}>{o.label || "（無題の選択肢）"}</button>
        );
      })}
      {values.filter((v) => !options.some((o) => o.v === v)).map((v) => (
        <button key={v} title="この値は親の選択肢に存在しません。タップで条件から除去します"
          onClick={() => { const next = values.filter((x) => x !== v); if (next.length === 0) onRemoveCond(); else onChange(next); }}
          style={dangerChip}>{DELETED_OPTION_LABEL} ×</button>
      ))}
    </>
  );
}

const numField: React.CSSProperties = { ...inp, width: 52, flex: "none", textAlign: "center", padding: "5px 6px", fontSize: 12 };
const dateField: React.CSSProperties = { ...inp, width: "auto", flex: "none", padding: "5px 8px", fontSize: 12, fontVariantNumeric: "tabular-nums" };
const isIntStr = (s: string): boolean => /^-?\d+$/.test(s);
// 手入力の数値を整数へ正規化 (先頭0除去 "05"→"5")。途中入力の "" / "-" はそのまま許容。
const normNum = (s: string): string => {
  if (s === "" || s === "-") return s;
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.trunc(n)) : s;
};

/** scale 親: 値 → 演算子(以上/以下/と同じ/範囲) の順。token を 1 つに集約する。 */
function ScalePredicate({ parent, token, onChange }: {
  parent: Extract<Question, { type: "scale" }>; token: string; onChange: (token: string) => void;
}) {
  const p = parseTokenPredicate(token);
  const op = p?.kind === "range" ? "range" : p?.kind === "cmp" && p.op !== ">" && p.op !== "<" ? p.op : ">=";
  const a = p?.kind === "range" ? p.lo : p?.kind === "cmp" ? p.operand : isIntStr(token) ? token : String(parent.scaleMin);
  const b = p?.kind === "range" ? p.hi : String(parent.scaleMax);
  // 空/途中入力 ("" / "-") は保存不能なので、確定時に scaleMin へフォールバックして token を常に有効に保つ。
  const fix = (x: string) => (x === "" || x === "-" ? String(parent.scaleMin) : x);
  const emit = (nop: string, na: string, nb: string) => onChange(nop === "range" ? `${fix(na)}..${fix(nb)}` : `${nop}${fix(na)}`);
  return (
    <>
      <input type="number" value={a} min={parent.scaleMin} max={parent.scaleMax} onChange={(e) => emit(op, normNum(e.target.value), b)} style={numField} />
      {op === "range" && (<><span style={{ color: T.faint }}>〜</span><input type="number" value={b} min={parent.scaleMin} max={parent.scaleMax} onChange={(e) => emit(op, a, normNum(e.target.value))} style={numField} /></>)}
      <select value={op} onChange={(e) => emit(e.target.value, a, b)} style={smallSelect}>
        <option value=">=">以上</option>
        <option value="<=">以下</option>
        <option value="=">と同じ</option>
        <option value="range">範囲</option>
      </select>
    </>
  );
}

/** date 親: 値 → 演算子(以降/以前/期間) の順。保存形式は YYYY/MM/DD。 */
function DatePredicate({ token, onChange }: { token: string; onChange: (token: string) => void }) {
  const p = parseTokenPredicate(token);
  const op = p?.kind === "range" ? "range" : p?.kind === "cmp" && (p.op === ">=" || p.op === "<=") ? p.op : ">=";
  const a = p?.kind === "range" ? p.lo : p?.kind === "cmp" ? p.operand : token || todayJst();
  const b = p?.kind === "range" ? p.hi : todayJst();
  const fix = (x: string) => x || todayJst(); // クリア時は今日にフォールバック (空 token 防止)。
  const emit = (nop: string, na: string, nb: string) => onChange(nop === "range" ? `${fix(na)}..${fix(nb)}` : `${nop}${fix(na)}`);
  return (
    <>
      <input type="date" value={toDateInput(a)} onChange={(e) => emit(op, fromDateInput(e.target.value), b)} style={dateField} />
      {op === "range" && (<><span style={{ color: T.faint }}>〜</span><input type="date" value={toDateInput(b)} onChange={(e) => emit(op, a, fromDateInput(e.target.value))} style={dateField} /></>)}
      <select value={op} onChange={(e) => emit(e.target.value, a, b)} style={smallSelect}>
        <option value=">=">以降</option>
        <option value="<=">以前</option>
        <option value="range">期間</option>
      </select>
    </>
  );
}

// 分岐条件を読み取り専用で明示する (raw ID を出さず phaseLabel で解決)。
function LockedBranchCondition({ phase, questions }: { phase: string; questions: Question[] }) {
  const conds = describePhase(phase, questions);
  if (!conds) return null;
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #E2E8F0", display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: T.faint, flexWrap: "wrap" }}>
      <Lock size={11} />
      <span>表示条件:</span>
      {conds.map((c, ci) => (
        <span key={ci} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          {ci > 0 && <span style={{ fontWeight: 700 }}>かつ</span>}
          <span style={{ fontWeight: 600, color: T.sub }}>{c.parentLabel}</span>
          <span>が</span>
          <span style={{ fontWeight: 600, color: T.sub }}>{c.valueLabels.join("、")}</span>
        </span>
      ))}
      <span>のとき</span>
    </div>
  );
}
